import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { usePhoenixRefresh } from './usePhoenixRefresh'
import { getCached, setCached } from '../lib/hookCache'

const BUCKET = 'exercise-photos'
const CACHE_SCOPE = 'exercises'

// Adds a `photoUrl` field by resolving the storage path via getPublicUrl.
// (The exercise-photos bucket is public-read so signed URLs aren't needed.)
function withPhotoUrls(rows) {
  return (rows || []).map((row) => {
    const path = row.machine_photo_url
    if (!path) return { ...row, photoUrl: null }
    if (path.startsWith('http')) return { ...row, photoUrl: path }
    const { data } = supabase.storage.from(BUCKET).getPublicUrl(path)
    return { ...row, photoUrl: data?.publicUrl || null }
  })
}

export function useExercises({ includeArchived = false } = {}) {
  const { user } = useAuth()
  const cacheKey = user ? `${user.id}:${includeArchived ? 'all' : 'active'}` : null
  const cached = getCached(CACHE_SCOPE, cacheKey)
  const [exercises, setExercises] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const initialLoadedRef = useRef(!!cached)

  const fetchExercises = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    let query = supabase
      .from('exercises')
      .select('*')
      .order('name', { ascending: true })
    if (!includeArchived) query = query.eq('is_archived', false)

    const { data, error: err } = await query
    if (err) {
      setError(err.message)
    } else {
      const decorated = withPhotoUrls(data)
      setExercises(decorated)
      if (cacheKey) setCached(CACHE_SCOPE, cacheKey, decorated)
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user, includeArchived, cacheKey])

  useEffect(() => {
    fetchExercises()
  }, [fetchExercises])

  usePhoenixRefresh(fetchExercises)

  const createExercise = async (values) => {
    if (!user) return null
    setError(null)
    const { data, error: err } = await supabase
      .from('exercises')
      .insert({
        user_id: user.id,
        name: values.name?.trim(),
        muscle_group: values.muscle_group,
        equipment_type: values.equipment_type,
        machine_photo_url: values.machine_photo_url || null,
        setup_notes: values.setup_notes?.trim() || null,
      })
      .select()
      .single()
    if (err) {
      setError(err.message)
      return null
    }
    await fetchExercises()
    return data
  }

  const updateExercise = async (id, values) => {
    if (!user) return false
    setError(null)
    const payload = { updated_at: new Date().toISOString() }
    if (values.name != null) payload.name = values.name.trim()
    if (values.muscle_group != null) payload.muscle_group = values.muscle_group
    if (values.equipment_type != null) payload.equipment_type = values.equipment_type
    if (values.machine_photo_url !== undefined) payload.machine_photo_url = values.machine_photo_url
    if (values.setup_notes !== undefined) payload.setup_notes = values.setup_notes?.trim() || null

    const { error: err } = await supabase
      .from('exercises')
      .update(payload)
      .eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetchExercises()
    return true
  }

  const archiveExercise = async (id) => {
    if (!user) return false
    setError(null)
    const { error: err } = await supabase
      .from('exercises')
      .update({ is_archived: true, updated_at: new Date().toISOString() })
      .eq('id', id)
    if (err) {
      setError(err.message)
      return false
    }
    await fetchExercises()
    return true
  }

  return {
    exercises,
    loading,
    error,
    createExercise,
    updateExercise,
    archiveExercise,
    refetch: fetchExercises,
  }
}
